import {DosageFormData} from "@/dosage/interfaces/DosageForm";
import {DosageTime, DosageTimeWithTime} from "@/dosage/interfaces/Dosage";
import DosageFormService from "@/dosage/services/DosageFormService";
import DosageService from "@/dosage/services/DosageService";
import {
    DosageDialogLine,
    TreatmentLine,
    TreatmentLineDosageDraft,
    TreatmentLineDosagePayload,
    TreatmentLineDosageProposal
} from "@/medication-checkup/interfaces/treatment-selections";

/**
 * The posologies edited along the review, kept by the scene until the
 * closing action carries them with the selection — a screen ritual, like the
 * checks: nothing is written before the collection closes.
 */
const TreatmentLineDosageService = () => {
    return {
        /**
         * The draft a line opens on: its served dosage when the record holds
         * one, the posology the reading proposed otherwise, an empty form
         * when neither exists.
         */
        draftOf(line: Pick<DosageDialogLine, 'dosage' | 'dosage_proposal'>): TreatmentLineDosageDraft {
            if (line.dosage) {
                const data = DosageFormService.mapExistingDosageToForm(line.dosage);

                return {
                    data: {...data, times: this.takes(data.times)},
                    comment: line.dosage.comment ?? '',
                };
            }

            if (line.dosage_proposal) {
                return this.proposalDraft(line.dosage_proposal);
            }

            return {data: DosageFormService.defaultFormValue(), comment: ''};
        },

        /**
         * A proposal as a draft of the form: the takes it names, on the
         * options it carries. Saving the dialog on it is how the pharmacist
         * accepts it.
         */
        proposalDraft(proposal: TreatmentLineDosageProposal): TreatmentLineDosageDraft {
            const times: DosageTime[] = proposal.times.map(time =>
                time.dosage_time_trigger_id !== null && time.dosage_time_trigger
                    ? {
                        dose: time.dose,
                        dosage_time_trigger_id: time.dosage_time_trigger_id,
                        dosage_time_trigger: time.dosage_time_trigger,
                    }
                    : {dose: time.dose, time: (time.time ?? '08:00') as DosageTimeWithTime['time']}
            );

            return {
                data: {
                    ...DosageFormService.defaultFormValue(),
                    times,
                    dose: proposal.dose,
                    frequency: proposal.frequency,
                    instruction: proposal.instruction,
                },
                comment: proposal.comment ?? '',
            };
        },

        /**
         * A copy of a draft the form can edit without touching the original
         * — the objects the scene holds are reactive, so a generic clone
         * refuses them.
         */
        clone(draft: TreatmentLineDosageDraft): TreatmentLineDosageDraft {
            const {data} = draft;

            return {
                data: {
                    times: data.times.map(time => ({...time})),
                    dose: data.dose ? {...data.dose} : null,
                    instruction: data.instruction ? {...data.instruction} : null,
                    frequency: data.frequency ? {...data.frequency} : null,
                    start: data.start ? new Date(data.start) : null,
                    end: data.end ? new Date(data.end) : null,
                    dates: data.dates ? data.dates.map(date => ({...date, value: new Date(date.value)})) : null,
                },
                comment: draft.comment,
            };
        },

        /**
         * The takes of a served dosage as the form tells them apart: the HDS
         * serves `dosage_time_trigger: null` on a take at a fixed time, and
         * the form reads any take carrying the key as a trigger.
         */
        takes(times: DosageTime[]): DosageTime[] {
            return times.map(time => {
                const trigger = 'dosage_time_trigger' in time ? time.dosage_time_trigger : null;

                return trigger
                    ? {id: time.id, dose: time.dose, dosage_time_trigger_id: trigger.id, dosage_time_trigger: trigger}
                    : {id: time.id, dose: time.dose, time: (time as DosageTimeWithTime).time};
            });
        },

        /**
         * The posology in words, as the line reads it.
         */
        describe(data: DosageFormData, comment: string): string {
            const times = data.times.filter(time => time.dose > 0);

            const words = times.length
                ? DosageService.humanizeDosage({
                    times,
                    dose: data.dose ?? undefined,
                    frequency: data.frequency ?? undefined,
                    instruction: data.instruction ?? undefined,
                })
                : 'Aucune prise définie';

            return [words, comment.trim()].filter(Boolean).join(' · ');
        },

        /**
         * How sure the reading was, in words.
         */
        confidenceLabel(confidence: TreatmentLineDosageProposal['confidence']): string {
            switch (confidence) {
                case 'high':
                    return 'lecture nette';
                case 'medium':
                    return 'lecture en partie déduite';
                default:
                    return 'lecture incertaine';
            }
        },

        /**
         * The draft as the closing action carries it: the dosage input less
         * what the server settles from the line — the product — or has no
         * use for in the collection — the period and the dates.
         */
        toPayload(line: TreatmentLine, draft: TreatmentLineDosageDraft): TreatmentLineDosagePayload {
            return {line_id: line.id, ...this.toDosage(line.product_id, draft)};
        },

        /**
         * A draft as an action of the review carries a posology: the dosage
         * input less what the server settles itself — the product, the
         * period and the dates.
         */
        toDosage(productId: number, draft: TreatmentLineDosageDraft): Omit<TreatmentLineDosagePayload, 'line_id'> {
            const input = DosageFormService.mapDosageFormDataToInput(productId, draft.data);

            return {
                dosage_dose_id: input.dosage_dose_id,
                dosage_frequency_id: input.dosage_frequency_id,
                dosage_instruction_id: input.dosage_instruction_id,
                times: input.times,
                comment: draft.comment.trim() || null,
            };
        },
    };
};

export default TreatmentLineDosageService();
